<h1>Security overview</h1>
<p class="tp-section-info" style="margin-bottom: 48px;">Your security score is an approximate representation of your team's overall security posture on TypeDB Cloud</p>

<section id="score" class="score-section">
  <div class="info-section">
    <h3 class="section-title-row">Security score -&nbsp;<span [style.color]="scoreColor$ | async">{{ securityLevel$ | async }}</span></h3>
    @if (securityLevel$ | async; as securityLevel) {
      @switch (securityLevel) {
        @case("low") {
          <p>Your TypeDB Cloud security score is low, representing substantial potential security risk.</p>
        }
        @case("medium") {
          <p>Your TypeDB Cloud security score is moderate, but still leaves notable security risks open.</p>
        }
        @case("high") {
          <p>Your TypeDB Cloud security score is great, indicating that your team is following best security practices.</p>
        }
      }
    }
  </div>
  <div class="gauge-section">
    <mwl-gauge
      [style.--gauge-color]="scoreColor$ | async"
      [max]="100"
      [dialRadius]="135/PI"
      [dialStartAngle]="0"
      [dialEndAngle]="180"
      [value]="(displayedValue$ | async) ?? 0"
      [class.full]="(displayedValue$ | async) === 100"
      [animated]="true"
      [animationDuration]="2"
    ></mwl-gauge>
    @if (securityLevel$ | async; as securityLevel) {
      @switch (securityLevel) {
        @case("low") {
          <p>At risk</p>
        }
        @case("medium") {
          <p>Needs improvement</p>
        }
        @case("high") {
          <p>Doing great</p>
        }
      }
    }
  </div>
</section>

<section id="recommendations">
  <h3 class="section-title-row">Security recommendations</h3>

  @if (securityRecommendations$ | async; as securityRecommendations) {
    @if (securityRecommendations.addSecurityContact) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-xmark"></i>Your team does not have a security contact</mat-panel-title>
        </mat-expansion-panel-header>
        <p>You don't have a security contact set, so you may not receive important TypeDB Cloud security information.</p>
        <p>Please <a routerLink="../config" fragment="security-contacts">add a security contact</a> so we can notify you of any security bulletins.</p>
      </mat-expansion-panel>
    }
    @if (securityRecommendations.reduceAdminCount) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-xmark"></i>Your team has too many members with admin privileges</mat-panel-title>
        </mat-expansion-panel-header>
        <p>You have {{ securityRecommendations.reduceAdminCount.adminCount }} admin users.</p>
        <p>
          We recommend limiting the number of admin users where possible,
          to reduce the impact of security breaches or accidental actions.
        </p>
        <p><a routerLink="../config" fragment="team-admins">View admins</a></p>
      </mat-expansion-panel>
    }
    @if (securityRecommendations.increaseMFAPolicy && securityRecommendations.increaseMFAPolicy.mfaPolicy === "none") {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <i class="fa-light fa-shield-xmark"></i>
            Your team does not have a secure MFA policy
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p>Your MFA policy is currently set to:&nbsp;<span class="mfa-none">{{ toSentenceCase(securityRecommendations.increaseMFAPolicy.mfaPolicy) }}</span></p>
        <p>By mandating MFA for all team members, you can help prevent unauthorized access to your TypeDB Cloud resources.</p>
        <p><a routerLink="../config" fragment="mfa-policy">Update your team's MFA policy</a></p>
      </mat-expansion-panel>
    }
    @if (securityRecommendations.enableMFA) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-xmark"></i>Some of your team members have not set up MFA</mat-panel-title>
        </mat-expansion-panel-header>
        <p>Some of your team members don't have MFA enabled, which puts their accounts - and by extension your team - at risk.</p>
        <p>The following users lack MFA:</p>
        <ul>
          @for (email of securityRecommendations.enableMFA.missingMFAEmails; track email) {
            <li>{{ email }}</li>
          }
        </ul>
      </mat-expansion-panel>
    }
    @if (securityRecommendations.setBackups) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-xmark"></i>Some of your clusters are not backed up</mat-panel-title>
        </mat-expansion-panel-header>
        <p>The following paid clusters lack an automated backup policy, putting your data at risk and reducing your recovery capabilities in case of an incident:</p>
        <ul>
          @for (cluster of securityRecommendations.setBackups.missingBackups; track cluster) {
            <li>{{ cluster }}</li>
          }
        </ul>
      </mat-expansion-panel>
    }

    @if (securityRecommendations.increaseMFAPolicy && securityRecommendations.increaseMFAPolicy.mfaPolicy === "encouraged") {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <i class="fa-light fa-shield-exclamation"></i>
            Your team does not have a secure MFA policy
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p>Your MFA policy is currently set to:&nbsp;<span class="mfa-encouraged">{{ toSentenceCase(securityRecommendations.increaseMFAPolicy.mfaPolicy) }}</span></p>
        <p>By mandating MFA for all team members, you can help prevent unauthorized access to your TypeDB Cloud resources.</p>
        <p><a routerLink="../config" fragment="mfa-policy">Update your team's MFA policy</a></p>
      </mat-expansion-panel>
    }

    @if (!securityRecommendations.addSecurityContact) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-check"></i>Your team has a designated security contact</mat-panel-title>
        </mat-expansion-panel-header>
        <p>You've got a security contact set, which allows us to contact you appropriately for important security information.</p>
      </mat-expansion-panel>
    }
    @if (!securityRecommendations.reduceAdminCount) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-check"></i>Your team has a secure IAM posture</mat-panel-title>
        </mat-expansion-panel-header>
        <p>You have fewer than 3 admin users.</p>
        <p>This helps you reduce the impact of security breaches or accidental actions.</p>
      </mat-expansion-panel>
    }
    @if (!securityRecommendations.increaseMFAPolicy) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-check"></i>Your team has a secure MFA policy</mat-panel-title>
        </mat-expansion-panel-header>
        <p>Your team mandates MFA for all team members, which helps prevent unauthorised access to your TypeDB CLoud resources.</p>
      </mat-expansion-panel>
    }
    @if (!securityRecommendations.enableMFA) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-check"></i>All team members have set up MFA</mat-panel-title>
        </mat-expansion-panel-header>
        <p>All of your team members have MFA enabled, which protects their accounts - and by extension your team.</p>
      </mat-expansion-panel>
    }
    @if (!securityRecommendations.setBackups) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title><i class="fa-light fa-shield-check"></i>All of your paid clusters are backed up</mat-panel-title>
        </mat-expansion-panel-header>
        <p>All of your paid clusters have automated backups, protecting your data and ensuring recovery potential in case of any incidents</p>
      </mat-expansion-panel>
    }
  }
</section>
